import { PdfChart, PdfChartProps } from "@app/components/Pdf";
import { ChartConfiguration, FontSpec } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { useTheme } from "@mui/material";
import { Image as PdfImage, Text, View } from "@react-pdf/renderer";
import { formatPercentFactor } from "@app/utils";
import { ChartDataset } from "chart.js";
import { CarPortfolioChartItem } from "@app/pages/assetAllocation/asset-allocation-types";
import { getMultiPeriodReturnComparisonChartData } from "@app/pages/assetAllocation/editPortfolioGroup/ChartMultiPeriodReturnComparison";
import { CarPdfBox } from "@app/components/PdfBox";
import { CarPdfChartLegend } from "@app/components/PdfChartLegend";
import { CarPdfChart, CarPdfChartProps } from "@app/components/PdfChart";
import { CarPortfolioAssetStatTableData } from "@app/pages/assetAllocation/usePortfolioGroupVersionStatTableData";
import { getHistoricalMarketsSummaryChartData } from "@app/pages/assetAllocation/editPortfolioGroup/ChartHistoricalMarketsSummary";

interface HistoricalMarketSummaryPdfChartProps
  extends Omit<CarPdfChartProps, "type" | "options" | "data" | "plugins"> {
  data: CarPortfolioAssetStatTableData;
  moduleId: string;
  isWhiteContext?: boolean;
}

export const HistoricalMarketSummaryPdfChart = ({
  data,
  moduleId,
  isWhiteContext,
  ...props
}: HistoricalMarketSummaryPdfChartProps) => {
  const theme = useTheme();

  const { datasets, yLabel, xLabels } = getHistoricalMarketsSummaryChartData({
    data,
    moduleId,
  });

  const font: Partial<FontSpec> = {
    size: 9,
    weight: 400,
    lineHeight: 1,
    family: theme.typography.fontFamily,
  };

  const titleFont: Partial<FontSpec> = {
    ...font,
    size: 12,
    weight: 600,
  };

  return (
    <CarPdfBox>
      <CarPdfChart
        type="bar"
        data={{ datasets }}
        options={{
          scales: {
            x: {
              type: "category",
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
              },
              grid: {
                display: false,
              },
              labels: xLabels,
            },
            y: {
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
                callback: (value) =>
                  typeof value === "number"
                    ? formatPercentFactor(value)
                    : value,
              },
              grid: ChartUtils.getGridOptions({ theme, highlightZero: true }),
              title: {
                display: true,
                text: yLabel,
                color: theme.palette.softBlack,
                font: titleFont,
              },
            },
          },
          plugins: {
            roundedBackground: ChartUtils.getRoundedBackgroundOptions({
              theme,
              isWhiteContext,
            }),
          },
        }}
        plugins={[ChartPlugins.roundedBackground]}
        {...props}
      />
      <CarPdfChartLegend
        style={{ marginTop: 20 }}
        items={datasets.map((i) => ({
          label: i.label ?? "",
          color: String(i.backgroundColor),
          isCircle: true,
        }))}
      />
    </CarPdfBox>
  );
};
